import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { optStr, useUrlText } from '@/lib/list-search';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, Eye, History, FileText, ExternalLink, Copy, Search, X, Tags } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { usePermissions } from '@/common/hooks/usePermissions';
import {
  passwordsService,
  passwordCategoriesService,
  Password,
  PasswordCategory,
} from '@/features/passwords/services/passwords.service';
import { useToast } from '@/hooks/use-toast';
import { PasswordFormDialog } from '@/features/passwords/components/PasswordFormDialog';
import { PasswordViewDialog } from '@/features/passwords/components/PasswordViewDialog';
import { PasswordHistoryDialog } from '@/features/passwords/components/PasswordHistoryDialog';
import { PasswordAccessLogsDialog } from '@/features/passwords/components/PasswordAccessLogsDialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotionBoards } from '@/features/passwords/hooks/useNotionBoards';
import { useTeams } from '@/features/passwords/hooks/useTeams';
import { PasswordCategoryFormDialog } from '@/features/passwords/components/PasswordCategoryFormDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface PasswordsSearch {
  search?: string;
  team?: string;
  category?: string;
  client?: string;
}

export const validatePasswordsSearch = (s: Record<string, unknown>): PasswordsSearch => ({
  search: optStr(s.search),
  team: optStr(s.team),
  category: optStr(s.category),
  client: optStr(s.client),
});

export const Route = createFileRoute('/_authenticated/passwords')({
  validateSearch: validatePasswordsSearch,
  component: PasswordsManagementPage,
});

function PasswordsManagementPage() {
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [formOpen, setFormOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedPassword, setSelectedPassword] = useState<Password | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PasswordCategory | null>(null);
  const urlSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const search = urlSearch.search ?? '';
  const selectedTeamId = urlSearch.team ?? '';
  const selectedCategoryId = urlSearch.category ?? '';
  const selectedNotionBoardId = urlSearch.client ?? '';
  const setParams = (patch: Partial<PasswordsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const [searchInput, setSearchInput] = useUrlText(urlSearch.search, (v) => setParams({ search: v }));
  const setSelectedTeamId = (v: string) => setParams({ team: v || undefined });
  const setSelectedCategoryId = (v: string) => setParams({ category: v || undefined });
  const setSelectedNotionBoardId = (v: string) => setParams({ client: v || undefined });
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  const canViewAll = hasPermission('passwords.view_all');
  const canEdit = hasPermission('passwords.edit');
  const canDelete = hasPermission('passwords.delete');

  // Buscar notion boards e teams para filtros
  const { data: notionBoards } = useNotionBoards();
  const { data: teams } = useTeams();
  const { data: categories } = useQuery({
    queryKey: ['password-categories'],
    queryFn: async () => {
      const response = await passwordCategoriesService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canViewAll,
  });

  // Buscar todas as senhas (só para gestores) com filtros
  const { data: passwordsData, isLoading } = useQuery({
    queryKey: ['passwords', search, selectedTeamId, selectedCategoryId, selectedNotionBoardId],
    queryFn: async () => {
      const response = await passwordsService.getAll({
        search: search || undefined,
        teamId: selectedTeamId || undefined,
        categoryId: selectedCategoryId || undefined,
        notionBoardId: selectedNotionBoardId || undefined,
      });
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canViewAll,
  });

  const passwords: Password[] = passwordsData || [];

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => passwordsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwords'] });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => passwordCategoriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['password-categories'] });
      queryClient.invalidateQueries({ queryKey: ['passwords'] });
      queryClient.invalidateQueries({ queryKey: ['my-passwords'] });
    },
  });

  const handleCreate = () => {
    setSelectedPassword(null);
    setFormOpen(true);
  };

  const handleEdit = (password: Password) => {
    setSelectedPassword(password);
    setFormOpen(true);
  };

  const handleDelete = async (password: Password) => {
    const ok = await confirm({
      title: `Excluir a senha de "${password.platform}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir senha',
      destructive: true,
    });
    if (ok) deleteMutation.mutate(password.id);
  };

  const handleView = (password: Password) => {
    setSelectedPassword(password);
    setViewDialogOpen(true);
  };

  const handleHistory = (password: Password) => {
    setSelectedPassword(password);
    setHistoryDialogOpen(true);
  };

  const handleLogs = (password: Password) => {
    setSelectedPassword(password);
    setLogsDialogOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['passwords'] });
    queryClient.invalidateQueries({ queryKey: ['my-passwords'] });
  };

  const handleCategorySuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['password-categories'] });
    queryClient.invalidateQueries({ queryKey: ['passwords'] });
    queryClient.invalidateQueries({ queryKey: ['my-passwords'] });
  };

  const handleCopyPassword = async (password: Password) => {
    try {
      const response = await passwordsService.getById(password.id, true);
      const passwordText = response.data.password;
      if (passwordText) {
        navigator.clipboard.writeText(passwordText);
        toast({
          title: 'Copiado!',
          description: 'Senha copiada para a área de transferência',
        });
        await passwordsService.logAccess(password.id, 'copy');
      }
    } catch (error: any) {
      console.error('Erro ao copiar senha:', error);
      // erro de API: o interceptor global já mostrou o toast
      if (error?.response) return;
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Não foi possível copiar a senha';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleCopyLogin = async (password: Password) => {
    if (password.login) {
      navigator.clipboard.writeText(password.login);
      toast({
        title: 'Copiado!',
        description: 'Login copiado para a área de transferência',
      });
      await passwordsService.logAccess(password.id, 'copy_login');
    } else {
      toast({
        title: 'Aviso',
        description: 'Esta senha não possui login cadastrado',
        variant: 'destructive',
      });
    }
  };

  if (!canViewAll) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Você não tem permissão para acessar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Gestão de Senhas"
        description="Gerencie todas as senhas do sistema"
        actions={
          canEdit && (
            <>
              <Button variant="outline" onClick={() => setCategoryManagerOpen(true)}>
                <Tags className="mr-2 h-4 w-4" />
                Categorias
              </Button>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Nova senha
              </Button>
            </>
          )
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Lista de Senhas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 mb-6">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Pesquisar por plataforma ou login..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('');
                        setParams({ search: undefined });
                      }}
                      aria-label="Limpar busca"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="relative">
                <Select value={selectedTeamId || undefined} onValueChange={setSelectedTeamId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por equipe" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams?.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTeamId && (
                  <button
                    type="button"
                    onClick={() => setSelectedTeamId('')}
                    className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center text-xs"
                    title="Limpar filtro de equipe"
                    aria-label="Limpar filtro de equipe"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="relative">
                <Select
                  value={selectedCategoryId || undefined}
                  onValueChange={setSelectedCategoryId}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCategoryId && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategoryId('')}
                    className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center text-xs"
                    title="Limpar filtro de categoria"
                    aria-label="Limpar filtro de categoria"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="relative">
                <Select value={selectedNotionBoardId || undefined} onValueChange={setSelectedNotionBoardId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {notionBoards?.map((board) => (
                      <SelectItem key={board.id} value={board.id}>
                        {board.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedNotionBoardId && (
                  <button
                    type="button"
                    onClick={() => setSelectedNotionBoardId('')}
                    className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center text-xs"
                    title="Limpar filtro de cliente"
                    aria-label="Limpar filtro de cliente"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              {(searchInput || search || selectedTeamId || selectedCategoryId || selectedNotionBoardId) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearchInput('');
                    navigate({ search: {}, replace: true });
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Limpar Filtros
                </Button>
              )}
            </div>
          </div>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Carregando...</p>
          ) : passwords.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma senha encontrada
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plataforma</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Equipes</TableHead>
                  <TableHead>Categorias</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {passwords.map((password) => (
                  <TableRow key={password.id}>
                    <TableCell className="font-medium">{password.platform}</TableCell>
                    <TableCell>
                      {password.notionBoard ? (
                        <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                          {password.notionBoard.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {password.teams && password.teams.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {password.teams.map((team) => (
                            <span
                              key={team.id}
                              className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs font-medium"
                            >
                              {team.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        'Nenhuma'
                      )}
                    </TableCell>
                    <TableCell>
                      {password.categories && password.categories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {password.categories.map((category) => (
                            <span
                              key={category.id}
                              className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                            >
                              {category.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {password.link ? (
                        <a
                          href={password.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          Abrir
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(password.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPassword(password)}
                          title="Copiar Senha"
                          aria-label={`Copiar senha de ${password.platform}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {password.login && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyLogin(password)}
                            title="Copiar Login"
                          aria-label={`Copiar login de ${password.platform}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(password)}
                          title="Ver Senha"
                          aria-label={`Ver senha de ${password.platform}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleHistory(password)}
                          title="Histórico"
                          aria-label={`Histórico de ${password.platform}`}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLogs(password)}
                          title="Logs de Acesso"
                          aria-label={`Logs de acesso de ${password.platform}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(password)}
                            title="Editar"
                          aria-label={`Editar ${password.platform}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(password)}
                            disabled={deleteMutation.isPending}
                            title="Deletar"
                          aria-label={`Excluir ${password.platform}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PasswordFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={handleSuccess}
        password={selectedPassword || undefined}
      />

      <PasswordViewDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        password={selectedPassword}
      />

      <PasswordHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        password={selectedPassword}
      />

      <PasswordAccessLogsDialog
        open={logsDialogOpen}
        onOpenChange={setLogsDialogOpen}
        password={selectedPassword}
      />

      <Dialog open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Categorias de Senha</DialogTitle>
            <DialogDescription>
              Crie e gerencie categorias para organizar os acessos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setSelectedCategory(null);
                  setCategoryFormOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Nova categoria
              </Button>
            </div>

            <div className="border rounded-md divide-y">
              {categories && categories.length > 0 ? (
                categories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-3"
                  >
                    <div>
                      <p className="font-medium">{category.name}</p>
                      {category.description && (
                        <p className="text-xs text-muted-foreground">
                          {category.description}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCategory(category);
                          setCategoryFormOpen(true);
                        }}
                        aria-label={`Editar categoria ${category.name}`}
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Remover a categoria "${category.name}"?`,
                            confirmLabel: 'Remover categoria',
                            destructive: true,
                          });
                          if (ok) deleteCategoryMutation.mutate(category.id);
                        }}
                        disabled={deleteCategoryMutation.isPending}
                        aria-label={`Remover categoria ${category.name}`}
                        title="Remover"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="p-4 text-sm text-muted-foreground">
                  Nenhuma categoria cadastrada.
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PasswordCategoryFormDialog
        open={categoryFormOpen}
        onOpenChange={setCategoryFormOpen}
        onSuccess={handleCategorySuccess}
        category={selectedCategory || undefined}
      />
    </div>
  );
}

